import {Component, OnInit, AfterViewInit} from '@angular/core';
import {BaseComponent} from '../../../base/base.component';
import {ActivatedRoute, Router} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';
import {
    CreateDaycareGuardianGQL,
    DaycareChildDetailFragment,
    DaycareChildGQL,
    RemoveDaycareGuardianGQL,
    UpdateDaycareChildGQL,
    UpdateDaycareGuardianGQL,
    User
} from '../../../../generated/graphql';
import {UserService} from '../../../service/user.service';

/**
 * Fatia 5D — DETALHE da criança + CRUD de guardiões. O trabalho nº 1 do gestor
 * aqui é COMPLETAR o documento dos guardiões sem documento (os 277 da carga
 * real) — por isso eles são DESTACADOS visualmente. Só admin/local-manager.
 */
@Component({
    selector: 'app-daycare-child-detail',
    templateUrl: './daycare-child-detail.component.html',
    styleUrls: ['./daycare-child-detail.component.scss']
})
export class DaycareChildDetailComponent extends BaseComponent implements OnInit, AfterViewInit {

    user = new User();
    childId: string;
    child: DaycareChildDetailFragment = null;
    loading = false;
    canWrite = false;

    // Form do guardião (inline).
    showGuardianForm = false;
    editingGuardianId: string = null;
    guardianForm = {kind: 'authorized', name: '', documentRaw: ''};

    // Form de edição da criança (inline).
    editingChild = false;
    childForm = {name: '', year: null};

    constructor(public router: Router,
                public activatedRoute: ActivatedRoute,
                public translate: TranslateService,
                public userService: UserService,
                private daycareChildGQL: DaycareChildGQL,
                private createGuardianGQL: CreateDaycareGuardianGQL,
                private updateGuardianGQL: UpdateDaycareGuardianGQL,
                private removeGuardianGQL: RemoveDaycareGuardianGQL,
                private updateChildGQL: UpdateDaycareChildGQL
    ) {
        super(router, translate);
    }

    ngOnInit() {
        this.childId = this.activatedRoute.snapshot.paramMap.get('id');
    }

    ngAfterViewInit(): void {
        this.userService.user.subscribe({
            next: data => {
                this.user = data as User;
                this.actionUserProfileBTN();
                if (this.actionUserProfile() === false) {
                    super.showMessage(this.translate.instant('atencao'), this.translate.instant('semacesso'), 'warning');
                    this.router.navigate(['/']);
                    return;
                }
                this.loadChild();
            }, error: err => super.onError(err)
        });
    }

    loadChild() {
        this.loading = true;
        this.daycareChildGQL.fetch({id: this.childId}, {fetchPolicy: 'network-only'}).subscribe({
            next: ({data}) => {
                this.child = data.daycareChild as DaycareChildDetailFragment;
                this.loading = false;
            }, error: err => {
                this.loading = false;
                super.onError(err);
            }
        });
    }

    guardianHasNoDocument(g: any): boolean {
        return !g || !g.documentRaw;
    }

    isParent(g: any): boolean {
        return !!g && g.kind === 'parent';
    }

    // ---------- Guardião ----------
    openAddGuardian() {
        this.editingGuardianId = null;
        this.guardianForm = {kind: 'authorized', name: '', documentRaw: ''};
        this.showGuardianForm = true;
    }

    openEditGuardian(g: any) {
        this.editingGuardianId = g.id;
        this.guardianForm = {kind: g.kind, name: g.name, documentRaw: g.documentRaw || ''};
        this.showGuardianForm = true;
    }

    cancelGuardian() {
        this.showGuardianForm = false;
    }

    saveGuardian() {
        if (!this.guardianForm.name || !this.guardianForm.name.trim()) {
            super.showMessage(this.translate.instant('atencao'), this.translate.instant('daycare.nomeobrigatorio'), 'warning');
            return;
        }
        this.loading = true;
        if (this.editingGuardianId) {
            this.updateGuardianGQL.mutate({
                data: {
                    id: this.editingGuardianId,
                    name: this.guardianForm.name,
                    documentRaw: this.guardianForm.documentRaw || '',
                    kind: this.guardianForm.kind
                }
            }).subscribe({
                next: ({data}) => this.onSaved(data.updateDaycareGuardian, 'daycare.guardiaoatualizado'),
                error: err => this.onWriteError(err)
            });
        } else {
            this.createGuardianGQL.mutate({
                data: {
                    childId: this.childId,
                    kind: this.guardianForm.kind,
                    name: this.guardianForm.name,
                    documentRaw: this.guardianForm.documentRaw || ''
                }
            }).subscribe({
                next: ({data}) => this.onSaved(data.createDaycareGuardian, 'daycare.guardiaoadicionado'),
                error: err => this.onWriteError(err)
            });
        }
    }

    removeGuardian(g: any) {
        super.confirmMessage(this.translate.instant('atencao'),
            this.translate.instant('daycare.confirmarremoverguardiao').replace('{name}', g.name), () => {
                this.loading = true;
                this.removeGuardianGQL.mutate({id: g.id}).subscribe({
                    next: () => {
                        super.showMessage(this.translate.instant('success'), this.translate.instant('daycare.guardiaoremovido'), 'success');
                        this.loadChild();
                    }, error: err => this.onWriteError(err)
                });
            });
    }

    // ---------- Criança ----------
    openEditChild() {
        this.childForm = {name: this.child.name, year: this.child.year};
        this.editingChild = true;
    }

    cancelChild() {
        this.editingChild = false;
    }

    saveChild() {
        if (!this.childForm.name || !this.childForm.name.trim()) {
            super.showMessage(this.translate.instant('atencao'), this.translate.instant('daycare.nomeobrigatorio'), 'warning');
            return;
        }
        this.loading = true;
        this.updateChildGQL.mutate({
            data: {id: this.childId, name: this.childForm.name, year: this.childForm.year ? Number(this.childForm.year) : null}
        }).subscribe({
            next: ({data}) => {
                this.child = data.updateDaycareChild as DaycareChildDetailFragment;
                this.editingChild = false;
                this.loading = false;
                super.showMessage(this.translate.instant('success'), this.translate.instant('daycare.criancaatualizada'), 'success');
            }, error: err => this.onWriteError(err)
        });
    }

    private onSaved(child: any, msgKey: string) {
        this.child = child as DaycareChildDetailFragment;
        this.showGuardianForm = false;
        this.loading = false;
        super.showMessage(this.translate.instant('success'), this.translate.instant(msgKey), 'success');
    }

    private onWriteError(err: any) {
        this.loading = false;
        super.onError(err);
    }

    actionUserProfile(): boolean {
        switch (this.user.profile) {
            case 'admin':
                return true;
            case 'local-manager':
                return true;
            default:
                return false;
        }
    }

    actionUserProfileBTN() {
        // admin e local-manager podem escrever (§47.2).
        this.canWrite = this.user.profile === 'admin' || this.user.profile === 'local-manager';
    }
}
